import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { studentApi } from '@/api';
import { Badge, Button, ConfirmDialog, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';
import { formatDate, formatFileSize } from '@/utils/format';

import { DocumentViewer } from './DocumentViewer';
import {
  DIPLOMA_ACCEPT,
  DIPLOMA_HINT,
  DIPLOMA_MAX_SIZE,
  DOCUMENT_TONES,
  STUDENT_DOCUMENT_LABELS,
} from './status';

/** What the student should know about the state their diploma is in. */
const EXPLANATIONS = {
  PENDING: 'Our team will check that it matches this entry. We will notify you when it is done.',
  VERIFIED:
    'Checked against this entry. Changing the degree, institution, grade or dates sends it back for checking.',
};

/**
 * The degree certificate behind one education entry, as a strip under it.
 *
 * Uploading is the request for verification -- there is no separate submit
 * button to forget -- so the one action offered without a file is "Upload".
 * A refusal shows its reason here, next to the button that fixes it.
 *
 * `entry` is an education row from `GET /students/me/education`.
 */
export function DiplomaPanel({ entry, onChanged }) {
  const toast = useToast();
  const input = useRef(null);
  const [removing, setRemoving] = useState(false);
  const [viewing, setViewing] = useState(false);

  const status = entry.diploma_status ?? 'UNVERIFIED';
  const hasFile = Boolean(entry.diploma_file_name);

  const upload = useMutation({
    mutationFn: (file) => studentApi.uploadDiploma(entry.id, file),
    onSuccess: () => {
      toast.success('Certificate uploaded. Our team will check it.');
      onChanged();
    },
    onError: (error) => toast.error(errorMessage(error, 'Upload failed')),
  });

  const remove = useMutation({
    mutationFn: () => studentApi.deleteDiploma(entry.id),
    onSuccess: () => {
      toast.success('Certificate removed');
      setRemoving(false);
      onChanged();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function choose(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    // The server checks the bytes too; this only spares a round trip.
    if (file.size > DIPLOMA_MAX_SIZE) {
      toast.error('File is too large. Maximum 5 MB.');
      return;
    }
    upload.mutate(file);
  }

  return (
    <div
      className={cn(
        'rounded-input border px-4 py-3',
        status === 'REJECTED'
          ? 'border-danger-200 bg-danger-50/50'
          : 'border-line bg-surface-muted/60',
      )}
    >
      <input
        ref={input}
        type="file"
        accept={DIPLOMA_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={choose}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'grid size-9 shrink-0 place-items-center rounded-lg',
              status === 'VERIFIED'
                ? 'bg-success-50 text-success-700'
                : 'bg-brand-50 text-brand-600',
            )}
          >
            <Icon name={status === 'VERIFIED' ? 'shieldCheck' : 'fileText'} size={17} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-ink truncate text-sm font-semibold">
                {hasFile ? entry.diploma_file_name : 'Upload related certificates'}
              </p>
              {hasFile && (
                <Badge tone={DOCUMENT_TONES[status] ?? 'neutral'} dot>
                  {STUDENT_DOCUMENT_LABELS[status] ?? status}
                </Badge>
              )}
            </div>
            <p className="text-ink-muted mt-0.5 text-xs">
              {hasFile
                ? [
                    formatFileSize(entry.diploma_size_bytes),
                    entry.diploma_submitted_at &&
                      `sent for checking ${formatDate(entry.diploma_submitted_at)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : `Upload your certificate to get this entry verified. ${DIPLOMA_HINT}.`}
            </p>
            {status === 'REJECTED' && entry.diploma_rejection_reason && (
              <p className="text-danger-800 mt-1.5 text-sm">
                <span className="font-semibold">Not accepted: </span>
                {entry.diploma_rejection_reason} Upload a new copy or correct the entry to have it
                checked again.
              </p>
            )}
            {EXPLANATIONS[status] && hasFile && (
              <p className="text-ink-muted mt-1.5 text-xs">{EXPLANATIONS[status]}</p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={hasFile ? 'secondary' : 'primary'}
            isLoading={upload.isPending}
            onClick={() => input.current?.click()}
          >
            <Icon name="upload" size={14} />
            {hasFile ? 'Replace' : 'Upload certificate'}
          </Button>
          {hasFile && (
            <>
              <Button
                size="sm"
                variant="secondary"
                title={`Open ${entry.diploma_file_name}`}
                onClick={() => setViewing(true)}
              >
                <Icon name="eye" size={14} />
                View
              </Button>
              <Button
                size="sm"
                variant="ghostDanger"
                title={`Remove the certificate for ${entry.degree}`}
                onClick={() => setRemoving(true)}
              >
                <Icon name="trash" size={14} />
                Remove
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Only when there is a file to show: an entry without a diploma has no
          type or name, and nothing to open. */}
      {hasFile && (
        <DocumentViewer
          open={viewing}
          onClose={() => setViewing(false)}
          title={`Certificate for ${entry.degree}`}
          fileName={entry.diploma_file_name}
          contentType={entry.diploma_content_type}
          queryKey={['diploma', entry.id]}
          loadUrl={(options) => studentApi.getDiplomaDownloadUrl(entry.id, options)}
        />
      )}

      <ConfirmDialog
        open={removing}
        title="Remove this certificate?"
        message={
          status === 'VERIFIED'
            ? 'This entry will no longer show as verified.'
            : 'You can upload it again at any time.'
        }
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onCancel={() => setRemoving(false)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}
